import React, { useState } from "react";
import { Search, Key, Eye, EyeOff, User, Mail, Gamepad2, Shield, CheckCircle } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const PasswordManagementTab = ({ language, t }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await axios.get("/api/admin/password-management/search?query=" + encodeURIComponent(query));
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleChangePassword = async () => {
    if (!selectedUser) {
      toast.error("Please select a user first");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post("/api/admin/password-management/change", {
        userId: selectedUser._id,
        newPassword: newPassword
      });
      toast.success("Password changed for " + response.data.username);
      setSelectedUser(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password change error:", error);
      toast.error(error.response?.data?.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="role-management">
      <div className="role-management-section">
        <h4 className="role-management-title">
          <Key className="w-5 h-5 inline mr-2" />
          Password Management
        </h4>
        <p className="text-gray-400 text-sm mb-4">
          Search for a user by Email, Username, or In-Game Name to change their password
        </p>
        <div className="role-search-section">
          <div className="role-search-container">
            <Search className="role-search-icon" />
            <input
              type="text"
              placeholder="Search by Email / Username / In-Game Name..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="role-search-input"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="role-search-results">
              {searchResults.map((user) => (
                <div key={user._id} onClick={() => handleSelectUser(user)} className="role-search-result-item">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <p className="role-search-result-name">{user.username}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {user.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Gamepad2 className="w-3 h-3" /> {user.inGameName}
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="w-3 h-3" /> {user.role}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {searchLoading && <div className="text-center py-2 text-gray-400">Searching...</div>}
        </div>
      </div>

      {selectedUser && (
        <div className="role-guide mt-6">
          <h4 className="role-guide-title">
            <CheckCircle className="w-5 h-5 inline mr-2 text-green-500" />
            Selected User
          </h4>
          <div className="selected-user-card mb-4">
            <div className="role-search-result-content">
              <div className="role-search-result-info">
                <h5 className="role-search-result-name text-lg">{selectedUser.username}</h5>
                <p className="role-search-result-email">
                  <Mail className="w-4 h-4 inline mr-1" /> {selectedUser.email}
                </p>
                <p className="role-search-result-stats">
                  <Gamepad2 className="w-4 h-4 inline mr-1" /> {selectedUser.inGameName}
                  <span className="mx-2">•</span>
                  <Shield className="w-4 h-4 inline mr-1" /> {selectedUser.role}
                </p>
              </div>
              <button onClick={() => setSelectedUser(null)} className="btn-compact btn-secondary">Clear</button>
            </div>
          </div>
          <div className="password-form-section bg-gray-800 rounded-lg p-4">
            <h5 className="text-white font-medium mb-4">Set New Password</h5>
            <div className="space-y-4">
              <div className="relative">
                <label className="text-gray-400 text-sm block mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="role-search-input w-full pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-sm block mb-1">Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="role-search-input w-full"
                />
              </div>
              {newPassword && newPassword.length < 6 && (
                <p className="text-red-400 text-sm">Password must be at least 6 characters</p>
              )}
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-400 text-sm">Passwords do not match</p>
              )}
              {newPassword && newPassword.length >= 6 && newPassword === confirmPassword && (
                <p className="text-green-400 text-sm">Passwords match</p>
              )}
              <button
                onClick={handleChangePassword}
                disabled={loading || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                className={"w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all " +
                  (loading || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white")}
              >
                <Key className="w-5 h-5" />
                {loading ? "Changing Password..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedUser && !searchQuery && (
        <div className="role-placeholder mt-6">
          <Key className="role-placeholder-icon" />
          <p className="role-placeholder-text">Search for a user to change their password</p>
        </div>
      )}
    </div>
  );
};

export default PasswordManagementTab;
